import type { DashboardCommon } from "@/api/custom_types";
import { LanguageSelector } from "@/components/base";
import { useTranslation } from "@/hooks";
import AddPaymentDialog from "@/pages/payment/shared/AddPaymentDialog";
import { userIntentAtom } from "@/store/login";
import { formatNumber } from "@/utils/tools";
import { Button, Icon } from "@st-marketplace/ui";
import { useAtomValue } from "jotai";
import {
	DashboardLayoutHeaderPlatformCurrency,
	DashboardLayoutHeaderStock,
	DashboardLayoutHeaderThemeMenu,
} from "../header/components";

interface DashboardLayoutMobileHeaderProps {
	data?: DashboardCommon;
	onDrawerOpen: () => void;
}

function DashboardLayoutMobileHeader({
	data,
	onDrawerOpen,
}: DashboardLayoutMobileHeaderProps) {
	const { t } = useTranslation();
	const userIntent = useAtomValue(userIntentAtom);

	const isBuyer = userIntent === "BUY";

	return (
		<header className="sticky top-0 z-30 border-b border-secondary-border bg-background-0">
			<div className="flex h-16 items-center px-3">
				<Button
					type="button"
					aria-label={t("header.openSidebar")}
					variant="text"
					color="ghost"
					icon
					className="rounded-lg"
					onClick={onDrawerOpen}
				>
					<Icon name="menu-line" />
				</Button>
				<div className="ms-auto flex items-center gap-3">
					{!isBuyer && <DashboardLayoutHeaderStock data={data} />}
					<DashboardLayoutHeaderThemeMenu />
					<LanguageSelector compact />
				</div>
			</div>
			{!isBuyer && (
				<div className="mx-3 mb-3 flex flex-col md:flex-row overflow-hidden rounded-lg border border-secondary-border bg-background-50 [&>*+*]:border-secondary-border [&>*+*]:border-s">
					<DashboardLayoutHeaderPlatformCurrency
						label={t("header.platformDepts")}
						amount={formatNumber(data?.platform_debts, "price")}
						compact
						className="border-b border-secondary-border max-sm:max-w-lg"
						// Always there: the chip shouldn't reflow when a debt is cleared,
						// and paying ahead of the next commission is allowed.
						action={
							<AddPaymentDialog
								target="platform"
								trigger={(open) => (
									<Button
										type="button"
										color="primary"
										size="sm"
										className="normal-case"
										onClick={open}
									>
										{t("header.payDebt")}
									</Button>
								)}
							/>
						}
					/>
					<DashboardLayoutHeaderPlatformCurrency
						label={t("header.platformFee")}
						caption={t("header.platformFeeCaption")}
						amount={formatNumber(data?.platform_commission, "price")}
						compact
					/>
				</div>
			)}
		</header>
	);
}

export default DashboardLayoutMobileHeader;
